import Area from '@/components/Area';
import {
  Environment,
  Hud,
  OrbitControls,
  PerspectiveCamera,
  RenderTexture,
  Text,
  OrthographicCamera,
  GizmoHelper,
} from '@react-three/drei';
import { Canvas, MeshProps, MeshStandardMaterialProps, useFrame, useThree } from '@react-three/fiber';
import * as React from 'react';
import {
  Camera,
  Matrix4,
  Mesh,
  PerspectiveCamera as PerspectiveCameraClass,
  OrthographicCamera as THREEOrthographicCamera,
} from 'three';

function Torus(props: React.ComponentProps<'mesh'>) {
  const [hovered, hover] = React.useState(false);
  return (
    <mesh onPointerOver={() => hover(true)} onPointerOut={() => hover(false)} {...props}>
      <torusGeometry args={[1, 0.25, 32, 100]} />
      {/* <meshStandardMaterial color={hovered ? 'hotpink' : 'orange'} /> */}
      <meshStandardMaterial color="orange" metalness={1} roughness={0.1} envMapIntensity={1} />
    </mesh>
  );
}

function useBoxState() {
  const [hovered, setHovered] = React.useState<number>();
  return {
    hovered,
    setHovered,
  };
}

type BoxContext = ReturnType<typeof useBoxState>;

const Context = React.createContext<BoxContext>({} as any);

const Box = React.forwardRef<Mesh, MeshProps & { rootCamera: Camera }>(({ children, rootCamera, ...props }, fref) => {
  const ref = React.useRef<Mesh>(null);
  const boxState = useBoxState();
  const { setHovered } = boxState;
  const [clicked, click] = React.useState(false);
  useFrame((state, delta) => ref.current?.rotation && (ref.current.rotation.x += delta));
  React.useImperativeHandle(fref, () => ref.current!, []);
  return (
    <mesh
      ref={ref}
      scale={clicked ? 1.5 : 1}
      onClick={() => {
        click(!clicked);
        rootCamera.position.set(0, 0, clicked ? 10 : 15);
      }}
      onPointerMove={(event) => {
        event.stopPropagation();
        setHovered(event.face?.materialIndex);
      }}
      onPointerOut={() => setHovered(undefined)}
      {...props}
    >
      <boxGeometry />
      <Context.Provider value={boxState}>{children}</Context.Provider>
    </mesh>
  );
});

function FaceMaterial({ children, index, ...props }: { index: number } & MeshStandardMaterialProps) {
  const { hovered } = React.useContext(Context);
  return (
    <meshStandardMaterial attach={`material-${index}`} color={hovered === index ? 'hotpink' : 'orange'} {...props}>
      <RenderTexture frames={6} attach="map" anisotropy={16}>
        <color attach="background" args={['white']} />
        <OrthographicCamera makeDefault left={-1} right={1} top={1} bottom={-1} position={[0, 0, 10]} zoom={0.5} />
        <Text color="black">{children}</Text>
      </RenderTexture>
    </meshStandardMaterial>
  );
}

function Viewcube({ renderPriority = 1, matrix = new Matrix4() }) {
  const mesh = React.useRef<Mesh>(null);
  const { camera, size } = useThree();
  useFrame(() => {
    // Spin mesh to the inverse of the default cameras matrix
    matrix.copy(camera.matrix).invert();
    mesh.current?.quaternion?.setFromRotationMatrix(matrix);
  });
  const refHudCamera = React.useRef<THREEOrthographicCamera>(null);
  React.useEffect(() => {
    const { width, height } = size;
    refHudCamera.current?.setViewOffset(width, height, -width / 3, height / 3, width, height);
  }, []);
  // console.log('Viewcube', { viewport });
  return (
    <Hud renderPriority={renderPriority}>
      <ambientLight intensity={Math.PI / 2} />
      <spotLight position={[10, 10, 10]} angle={0.15} penumbra={1} decay={0} intensity={Math.PI} />
      <pointLight position={[-10, -10, -10]} decay={0} intensity={Math.PI} />
      {/* <PerspectiveCamera ref={refHudCamera} makeDefault position={[0, 0, 10]} /> */}
      <OrthographicCamera
        ref={refHudCamera}
        makeDefault
        zoom={40}
        // manual
        // left={-2}
        // right={2}
        // top={2}
        // bottom={-2}
        // near={0.1}
        // far={100}
        // view={{
        //   enabled: true,
        //   fullWidth: viewport.width,
        //   fullHeight: viewport.height,
        //   offsetX: 0,
        //   offsetY: 0,
        //   width: 10,
        //   height: 10,
        // }}
        position={[0, 0, 10]}
        // viewport={new Vector4(1, 1, 2, 2)}
      />

      <Box rootCamera={camera} ref={mesh} position={[0, 0, 0]}>
        <FaceMaterial index={0}>front</FaceMaterial>
        <FaceMaterial index={1}>back</FaceMaterial>
        <FaceMaterial index={2}>top</FaceMaterial>
        <FaceMaterial index={3}>bottom</FaceMaterial>
        <FaceMaterial index={4}>left</FaceMaterial>
        <FaceMaterial index={5}>right</FaceMaterial>
      </Box>
      <gridHelper args={[5, 5, 0xff0000, 'teal']} />
      <axesHelper />
    </Hud>
  );
}

export type Props = {};

function App01(props: Props) {
  const {} = props;
  return (
    <Area>
      {() => (
        <>
          <Canvas camera={{ position: [5, 5, 10], fov: 50 }}>
            <ambientLight intensity={0.5 * Math.PI} />
            <Torus scale={1.75}></Torus>
            <Viewcube />
            <OrbitControls />
            <Environment preset="city" />
            {/* <GizmoHelper /> */}
            <gridHelper />
            <axesHelper args={[5]} />
          </Canvas>
        </>
      )}
    </Area>
  );
}

export default React.memo(App01);
